// @ts-strict-ignore
import * as React from 'react';
import { AppComponentProps } from '@/typescriptHelpers';
import Descriptions from 'antd/lib/descriptions';
import Text from 'antd/lib/typography/Text';
import { truncate } from 'lodash';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectAddFixRequestsToOpenPRsAgainstReleaseBranches,
    selectAppSettingAutomations,
    selectAppSettings,
    selectEnableStatusChecksForFixRequests,
    selectFixRequestsAutoMergeUserCreatedPRsSettings,
    selectFixRequestsDetailsTemplate,
    selectPreventFixRequestsAfterSubmission,
    selectPreventFixRequestsAfterSubmissionCustomCopyEnabled,
    selectPreventFixRequestsAfterSubmissionCustomCopy,
    selectRequiredFixRequestsApprovals,
    selectRequiresFixRequestsApprovals,
    selectRequiresFixRequestsDetails,
    selectThreadFixNotifications,
    selectVCSSupportsStatusChecks,
} from '../../selectors/selectors';
import { AutomationType, IntegrationType, MergeType } from '../../types';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { EditOutlined } from '@ant-design/icons';
import EditFixesSettingsModal from './EditFixesSettingsModal';
import PullFixesIntoTheReleaseAutomationSettingsBanner from './PullFixesIntoTheReleaseAutomationSettingsBanner';
import { Flex } from 'antd/lib';

const FixRequestsSettings: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const appSettings = useSelector((state: RootState) => selectAppSettings(state, props.appId));

    const autoCherryPickAutomationId = useSelector((state: RootState) => {
        const automations = selectAppSettingAutomations(state, props.appId);
        const autoCherryPickAutomation = automations.find(
            (automation) => automation.type === AutomationType.AutoCherryPickFromWorkingBranch,
        );
        return autoCherryPickAutomation?.id;
    });

    const addFixRequestsToOpenPRsAgainstReleaseBranches = useSelector(
        selectAddFixRequestsToOpenPRsAgainstReleaseBranches(props.appId),
    );
    const requiresFixRequestsApprovals = useSelector(selectRequiresFixRequestsApprovals(props.appId));
    const requiredFixRequestsApprovals = useSelector(selectRequiredFixRequestsApprovals(props.appId));
    const vcsSupportsStatusChecks = useSelector(selectVCSSupportsStatusChecks(props.appId, props.orgId));
    const enableStatusChecksForFixRequests = useSelector(selectEnableStatusChecksForFixRequests(props.appId));
    const requiresFixRequestsDetails = useSelector(selectRequiresFixRequestsDetails(props.appId));
    const fixRequestsDetailsTemplate = useSelector(selectFixRequestsDetailsTemplate(props.appId));
    const threadRelatedFixNotifications = useSelector(selectThreadFixNotifications(props.appId));
    const preventFixRequestsAfterSubmissionCustomCopy = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopy(props.appId),
    );
    const preventFixRequestsAfterSubmissionCustomCopyEnabled = useSelector(
        selectPreventFixRequestsAfterSubmissionCustomCopyEnabled(props.appId),
    );
    const preventFixRequestsAfterSubmission = useSelector(selectPreventFixRequestsAfterSubmission(props.appId));
    const { mergeUserCreatedPRs, workingBranchUserCreatedPRMergeType, releaseBranchUserCreatedPRMergeType } =
        useSelector(selectFixRequestsAutoMergeUserCreatedPRsSettings(props.appId));

    const pullFixesIntoTheReleaseAutomation = useSelector((state: RootState) => {
        const automations = selectAppSettingAutomations(state, props.appId);
        return automations.find((automation) => automation.type === AutomationType.AutoCherryPickFromWorkingBranch);
    });

    const isPullFixesIntoTheReleaseAutomationEnabled = pullFixesIntoTheReleaseAutomation?.enabled ?? false;

    const hasSlackIntegration = appSettings.integrations.some(
        (integration) => integration.id === IntegrationType.Slack,
    );

    const [showEditFixesSettingsModal, setShowEditFixesSettingsModal] = React.useState(false);

    const displayMergeStrategyValue = (strategy: MergeType) => {
        switch (strategy) {
            case MergeType.DoNotMerge:
                return 'Do not merge user-created PRs';
            case MergeType.MergeCommit:
                return 'Merge user-created PRs with merge commit';
            case MergeType.Squash:
                return 'Squash merge user-created PRs';
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Descriptions
                size="middle"
                layout="horizontal"
                bordered
                column={1}
                labelStyle={{ width: 300 }}
                extra={
                    <PermissionedActionButton
                        actionType={RunwayUserAction.UAUpdateAppSettings}
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => setShowEditFixesSettingsModal(true)}
                    >
                        Edit
                    </PermissionedActionButton>
                }
            >
                <Descriptions.Item label="Create a fix for open PRs against the release branch">
                    {addFixRequestsToOpenPRsAgainstReleaseBranches ? 'Yes' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Auto-merge user-created PRs with fixes enabled">
                    {mergeUserCreatedPRs ? 'Yes' : 'No'}
                </Descriptions.Item>
                {mergeUserCreatedPRs && (
                    <Descriptions.Item label="Working branch auto-merge strategy">
                        {displayMergeStrategyValue(workingBranchUserCreatedPRMergeType)}
                    </Descriptions.Item>
                )}
                {mergeUserCreatedPRs && (
                    <Descriptions.Item label="Release branch auto-merge strategy">
                        {displayMergeStrategyValue(releaseBranchUserCreatedPRMergeType)}
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Fix approvals enabled">
                    {requiresFixRequestsApprovals ? 'Yes' : 'No'}
                </Descriptions.Item>
                {requiresFixRequestsApprovals && (
                    <Descriptions.Item label="Number of approvals required">
                        {requiredFixRequestsApprovals}
                    </Descriptions.Item>
                )}
                {vcsSupportsStatusChecks && (
                    <Descriptions.Item label="Status checks enabled">
                        {requiresFixRequestsApprovals && enableStatusChecksForFixRequests ? 'Yes' : 'No'}
                    </Descriptions.Item>
                )}
                <Descriptions.Item label="Fix details required">
                    {requiresFixRequestsDetails ? 'Yes' : 'No'}
                </Descriptions.Item>
                <Descriptions.Item label="Fix details template">
                    {fixRequestsDetailsTemplate != '' ? truncate(fixRequestsDetailsTemplate, { length: 65 }) : 'None'}
                </Descriptions.Item>
                <Descriptions.Item label="Prevent fix requests once app is in review">
                    <Flex vertical>
                        <Text>{preventFixRequestsAfterSubmission ? 'Yes' : 'No'}</Text>
                        {preventFixRequestsAfterSubmissionCustomCopyEnabled && preventFixRequestsAfterSubmission ?
                            <Text>
                                <b>Additional copy:</b>{' '}
                                {truncate(preventFixRequestsAfterSubmissionCustomCopy, { length: 50 })}
                            </Text>
                        :   null}
                    </Flex>
                </Descriptions.Item>
                {hasSlackIntegration && (
                    <Descriptions.Item label="Thread related fix notifications">
                        {threadRelatedFixNotifications ? 'Yes' : 'No'}
                    </Descriptions.Item>
                )}
            </Descriptions>
            <div style={{ marginTop: 16 }}>
                <PullFixesIntoTheReleaseAutomationSettingsBanner
                    appId={props.appId}
                    orgId={props.orgId}
                    isAutoCherryPickAutomationEnabled={isPullFixesIntoTheReleaseAutomationEnabled}
                    autoCherryPickAutomationId={autoCherryPickAutomationId}
                    bannerText={
                        isPullFixesIntoTheReleaseAutomationEnabled ?
                            <Text style={{ flexGrow: 1, marginRight: 15 }}>
                                &quot;Pull (cherry-pick) fixes into the release&quot; (automation) is <b>enabled</b>.
                                You can disable or manage options from its settings.
                            </Text>
                        :   <Text type="secondary" style={{ flexGrow: 1, marginRight: 15 }}>
                                &quot;Pull (cherry-pick) fixes into the release&quot; (automation) is <b>disabled</b>.
                                Any approved fixes will need to be manually cherry-picked and merged. You can enable and
                                manage options from the automation’s settings.
                            </Text>
                    }
                />
            </div>
            <EditFixesSettingsModal
                open={showEditFixesSettingsModal}
                appId={props.appId}
                orgId={props.orgId}
                onCancel={() => {
                    setShowEditFixesSettingsModal(false);
                }}
            />
        </div>
    );
};

export default FixRequestsSettings;
